import React, { useMemo } from 'react';
import { mdiCodeBraces, mdiLightningBolt } from '@mdi/js';
import Icon from '@mdi/react';
import { renderDate, Variables } from '../../../..';
import * as Cd from './coding-detail.styles';
import { computeCodingFieldChanges, formatCodingChangesSummary } from './coding-detail.utils';
import { PayloadChangesCard } from './payload-changes-card';
import { WorkerFunction } from './worker-function-detail.types';
import { getWorkerFunctionPayload } from './worker-function-detail.utils';

interface IWorkerFunctionEmbeddedPanelProps {
    embedded?: boolean;
    workerFunction: WorkerFunction;
}

export const WorkerFunctionEmbeddedPanel: React.FunctionComponent<IWorkerFunctionEmbeddedPanelProps> = ({
    embedded = true,
    workerFunction,
}) => {
    const beforePayload = useMemo(() => getWorkerFunctionPayload(workerFunction, 'before'), [workerFunction]);
    const afterPayload = useMemo(() => getWorkerFunctionPayload(workerFunction, 'after'), [workerFunction]);

    // Comparing against a side that was not recorded would list every field as added or removed,
    // which reads as the function rewriting the claim — so say it was not recorded instead.
    const isComparable = beforePayload != null && afterPayload != null;
    const fieldChanges = useMemo(
        () => (isComparable ? computeCodingFieldChanges(beforePayload, afterPayload) : []),
        [isComparable, beforePayload, afterPayload],
    );
    const changesSummary = isComparable ? formatCodingChangesSummary(fieldChanges) : 'not compared';
    const notComparedMessage = isComparable
        ? undefined
        : 'The before or after payload was not recorded for this run, so no changes are shown.';

    const code = workerFunction.code?.trim();

    return (
        <Cd.CdPanel $embedded={embedded}>
            <Cd.CdHeadlineCard $embedded={embedded}>
                <Cd.CdIconWrap>
                    <Icon path={mdiCodeBraces} size={0.9} color={Variables.ThemeLight.PrimaryConstant6} />
                </Cd.CdIconWrap>
                <Cd.CdHeadlineMain>
                    <Cd.CdHeadlineTitleRow>
                        <Cd.CdHeadlineTitle>Worker Function Executed</Cd.CdHeadlineTitle>
                    </Cd.CdHeadlineTitleRow>
                    <Cd.CdHeadlineMeta>
                        {workerFunction.ruleId ? (
                            <>
                                Run by rule <strong>{workerFunction.ruleId}</strong>
                            </>
                        ) : (
                            'Run by a worker rule'
                        )}
                        {workerFunction.created ? <> · {renderDate(workerFunction.created)}</> : null}
                    </Cd.CdHeadlineMeta>
                    <Cd.CdHeadlineSummary>
                        <Icon path={mdiLightningBolt} size={0.67} color={Variables.ThemeLight.AttentionConstant6} />
                        <span>
                            <strong>{changesSummary}</strong>
                        </span>
                    </Cd.CdHeadlineSummary>
                </Cd.CdHeadlineMain>
                <Cd.CdHeadlineAside>
                    <Cd.CdLabel>Function ID</Cd.CdLabel>
                    <Cd.CdMonoValue>{workerFunction.id}</Cd.CdMonoValue>
                </Cd.CdHeadlineAside>
            </Cd.CdHeadlineCard>

            <Cd.CdSectionCard $embedded={embedded}>
                <Cd.CdSectionTitle>Script</Cd.CdSectionTitle>
                {code ? (
                    <Cd.CdCodeBlock>{code}</Cd.CdCodeBlock>
                ) : (
                    <Cd.CdEmptyState>No script was recorded for this run.</Cd.CdEmptyState>
                )}
            </Cd.CdSectionCard>

            <PayloadChangesCard
                embedded={embedded}
                beforePayload={beforePayload}
                afterPayload={afterPayload}
                fieldChanges={fieldChanges}
                notComparedMessage={notComparedMessage}
                noAfterPayloadMessage="No after payload was recorded for this run."
            />
        </Cd.CdPanel>
    );
};
